
import { useState, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface UnsplashImage {
  id: string;
  urls: {
    raw: string;
    full: string;
    regular: string;
    small: string;
    thumb: string;
  };
  description: string | null;
  alt_description?: string | null;
  user: {
    name: string;
    username: string;
    links: {
      html: string;
    };
  };
  links: {
    html: string;
  };
  width: number;
  height: number;
}

export interface SearchResults {
  results: UnsplashImage[];
  total: number;
  total_pages: number;
}

interface UseUnsplashImagesReturn {
  searchImages: (query: string, page?: number, perPage?: number) => Promise<SearchResults>;
  getPhoto: (id: string) => Promise<UnsplashImage | null>;
  isLoading: boolean;
  error: string | null;
}

export function useUnsplashImages(): UseUnsplashImagesReturn {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Use a ref to track requests and prevent stale callbacks from updating state
  const requestIdRef = useRef(0);

  // Helper function to validate image URLs
  const validateImageUrls = useCallback((urls: any): boolean => {
    if (!urls || typeof urls !== 'object') {
      console.error("Invalid URLs object:", urls);
      return false;
    }
    
    // Check if at least one valid URL exists
    return !!(urls.small || urls.thumb || urls.regular || urls.full || urls.raw);
  }, []);

  // Search for images
  const searchImages = useCallback(async (
    query: string,
    page = 1,
    perPage = 12
  ): Promise<SearchResults> => {
    // Generate a new request ID to track this specific request
    const requestId = ++requestIdRef.current;
    console.log(`Starting Unsplash search request #${requestId} for: "${query}", page: ${page}`);
    
    setIsLoading(true);
    setError(null);

    try {
      const startTime = Date.now();
      const { data, error: apiError } = await supabase.functions.invoke('unsplash-images', {
        body: {
          action: 'search',
          query,
          page: page.toString(),
          perPage: perPage.toString(),
        },
      });
      const requestTime = Date.now() - startTime;
      
      // Check if this is still the most recent request
      if (requestId !== requestIdRef.current) {
        console.log(`Abandoning stale Unsplash search request #${requestId} (current: #${requestIdRef.current})`);
        return { results: [], total: 0, total_pages: 0 };
      }
      
      console.log(`Unsplash API request #${requestId} completed in ${requestTime}ms`);

      if (apiError) {
        console.error(`Unsplash request #${requestId} error:`, apiError);
        setError(`Failed to search images: ${apiError.message || "Unknown error"}`);
        toast.error("Error searching images");
        return { results: [], total: 0, total_pages: 0 };
      }

      if (!data) {
        console.error(`Unsplash request #${requestId}: No data returned from API`);
        setError("No data returned from API");
        return { results: [], total: 0, total_pages: 0 };
      }

      // Validate the response structure
      if (!Array.isArray(data.results)) {
        console.error(`Unsplash request #${requestId}: Invalid data format - results is not an array:`, 
          typeof data, Object.keys(data));
        setError("Invalid response format from API");
        return { results: [], total: 0, total_pages: 0 };
      }

      // Log the structure of the first result
      if (data.results.length > 0) {
        console.log(`Unsplash request #${requestId}: First result has valid URLs:`, 
          validateImageUrls(data.results[0]?.urls));
      }

      // Create a properly formatted and validated results object
      const validatedResults: UnsplashImage[] = data.results.map((item: any) => {
        if (!item || typeof item !== 'object') {
          console.warn(`Unsplash request #${requestId}: Invalid item in results array:`, item);
          // Return a placeholder image for invalid items
          return {
            id: `invalid-${Math.random().toString(36).substring(2, 9)}`,
            urls: {
              raw: "",
              full: "",
              regular: "",
              small: "",
              thumb: ""
            },
            description: "Invalid image data",
            alt_description: null,
            user: {
              name: "Unknown",
              username: "unknown",
              links: { html: "https://unsplash.com" }
            },
            links: { html: "https://unsplash.com" },
            width: 0,
            height: 0
          };
        }

        // Create a valid image object with default values for missing properties
        const validatedImage: UnsplashImage = {
          id: item.id || `unknown-${Math.random().toString(36).substring(2, 9)}`,
          urls: {
            raw: item.urls?.raw || "",
            full: item.urls?.full || "",
            regular: item.urls?.regular || "",
            small: item.urls?.small || "",
            thumb: item.urls?.thumb || ""
          },
          description: item.description || item.alt_description || "Unsplash image",
          alt_description: item.alt_description || null,
          user: {
            name: item.user?.name || "Unknown photographer",
            username: item.user?.username || "unknown",
            links: { 
              html: item.user?.links?.html || "https://unsplash.com" 
            }
          },
          links: { 
            html: item.links?.html || "https://unsplash.com" 
          },
          width: item.width || 0,
          height: item.height || 0
        };
        
        return validatedImage;
      });

      const result: SearchResults = {
        results: validatedResults,
        total: data.total || 0,
        total_pages: data.total_pages || 0
      };

      console.log(`Unsplash request #${requestId} complete: ${result.results.length} valid images`);
      return result;
    } catch (err: any) {
      // Check if this is still the most recent request
      if (requestId !== requestIdRef.current) {
        console.log(`Error in abandoned Unsplash search request #${requestId}`);
        return { results: [], total: 0, total_pages: 0 };
      }
      
      console.error(`Error in Unsplash search request #${requestId}:`, err);
      setError(`An unexpected error occurred: ${err.message || "Unknown error"}`);
      toast.error("Failed to load images");
      return { results: [], total: 0, total_pages: 0 };
    } finally {
      // Only update loading state if this is still the current request
      if (requestId === requestIdRef.current) {
        setIsLoading(false);
      }
    }
  }, [validateImageUrls]);

  // Get details for a specific photo
  const getPhoto = useCallback(async (id: string): Promise<UnsplashImage | null> => {
    // Generate a new request ID for this request
    const requestId = ++requestIdRef.current;
    console.log(`Starting Unsplash photo request #${requestId} for ID: ${id}`);
    
    setIsLoading(true);
    setError(null);

    try {
      const startTime = Date.now();
      const { data, error: apiError } = await supabase.functions.invoke('unsplash-images', {
        body: {
          action: 'photo',
          id,
        },
      });
      const requestTime = Date.now() - startTime;
      
      // Check if this is still the most recent request
      if (requestId !== requestIdRef.current) {
        console.log(`Abandoning stale Unsplash photo request #${requestId} (current: #${requestIdRef.current})`);
        return null;
      }
      
      console.log(`Unsplash photo API request #${requestId} completed in ${requestTime}ms`);

      if (apiError) {
        console.error(`Unsplash photo request #${requestId} error:`, apiError);
        setError(`Failed to get photo details: ${apiError.message || "Unknown error"}`);
        toast.error("Failed to load image details");
        return null;
      }

      if (!data || !data.photo) {
        console.error(`Unsplash photo request #${requestId}: Invalid photo data:`, data);
        setError("Invalid photo data format");
        return null;
      }

      // Validate the photo object
      const photo = data.photo;
      
      if (!validateImageUrls(photo.urls)) {
        console.error(`Unsplash photo request #${requestId}: Photo has invalid URLs:`, photo.urls);
        setError("Photo has invalid URLs");
        return null;
      }
      
      const validatedPhoto: UnsplashImage = {
        id: photo.id || id,
        urls: {
          raw: photo.urls?.raw || "",
          full: photo.urls?.full || "",
          regular: photo.urls?.regular || "",
          small: photo.urls?.small || "",
          thumb: photo.urls?.thumb || ""
        },
        description: photo.description || photo.alt_description || "Unsplash image",
        alt_description: photo.alt_description || null,
        user: {
          name: photo.user?.name || "Unknown photographer",
          username: photo.user?.username || "unknown",
          links: { 
            html: photo.user?.links?.html || "https://unsplash.com" 
          }
        },
        links: { 
          html: photo.links?.html || "https://unsplash.com" 
        },
        width: photo.width || 0,
        height: photo.height || 0
      };
      
      console.log(`Unsplash photo request #${requestId} complete with valid URLs:`, 
        validateImageUrls(validatedPhoto.urls));
      
      return validatedPhoto;
    } catch (err: any) {
      // Check if this is still the most recent request
      if (requestId !== requestIdRef.current) {
        console.log(`Error in abandoned Unsplash photo request #${requestId}`);
        return null;
      }
      
      console.error(`Error in Unsplash photo request #${requestId}:`, err);
      setError(`An unexpected error occurred: ${err.message || "Unknown error"}`);
      toast.error("Failed to load image details");
      return null;
    } finally {
      // Only update loading state if this is still the current request
      if (requestId === requestIdRef.current) {
        setIsLoading(false);
      }
    }
  }, [validateImageUrls]);

  return {
    searchImages,
    getPhoto,
    isLoading,
    error,
  };
}
